import { fetchApi } from "@/lib/apiBase";

const DAY_ORDER = [
  { key: "monday", full: "Monday", short: "Mon" },
  { key: "tuesday", full: "Tuesday", short: "Tue" },
  { key: "wednesday", full: "Wednesday", short: "Wed" },
  { key: "thursday", full: "Thursday", short: "Thu" },
  { key: "friday", full: "Friday", short: "Fri" },
  { key: "saturday", full: "Saturday", short: "Sat" },
  { key: "sunday", full: "Sunday", short: "Sun" },
];

const DEFAULT_WEEKLY_HOURS = [
  { day_key: "monday", enabled: true, start_time: "11:30", end_time: "20:00" },
  { day_key: "tuesday", enabled: true, start_time: "11:30", end_time: "20:00" },
  { day_key: "wednesday", enabled: true, start_time: "11:30", end_time: "20:00" },
  { day_key: "thursday", enabled: true, start_time: "11:30", end_time: "20:00" },
  { day_key: "friday", enabled: true, start_time: "14:00", end_time: "20:00" },
  { day_key: "saturday", enabled: true, start_time: "10:00", end_time: "17:00" },
  { day_key: "sunday", enabled: false, start_time: "10:00", end_time: "17:00" },
];

export const DEFAULT_PUBLIC_HOURS_ROWS = buildPublicHoursRows();
export const DEFAULT_GROUPED_PUBLIC_HOURS_ROWS = buildGroupedPublicHoursRows();

let publicSettingsCache = null;
let publicSettingsPromise = null;

export function invalidatePublicAvailabilitySettingsCache() {
  publicSettingsCache = null;
  publicSettingsPromise = null;
}

function formatClockTime(value) {
  if (!value) {
    return "";
  }

  const [hoursString = "0", minutesString = "00"] = String(value).split(":");
  const hours = Number.parseInt(hoursString, 10);
  const minutes = Number.parseInt(minutesString, 10);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return String(value);
  }

  const normalizedHours = ((hours % 24) + 24) % 24;
  const period = normalizedHours >= 12 ? "PM" : "AM";
  const hour12 = normalizedHours % 12 || 12;
  const paddedMinutes = String(minutes).padStart(2, "0");

  return `${hour12}:${paddedMinutes} ${period}`;
}

function formatHoursRange(day) {
  if (!day?.enabled) {
    return "Closed";
  }

  return `${formatClockTime(day.start_time)} - ${formatClockTime(day.end_time)}`;
}

function withDayOrder(hours) {
  const map = new Map((hours || []).map((item) => [item.day_key, item]));
  return DAY_ORDER.map((day) => ({
    ...day,
    ...(map.get(day.key) || DEFAULT_WEEKLY_HOURS.find((item) => item.day_key === day.key)),
  }));
}

export function buildPublicHoursRows(weeklyHours = DEFAULT_WEEKLY_HOURS) {
  return withDayOrder(weeklyHours).map((day) => ({
    key: day.key,
    day: day.full,
    shortDay: day.short,
    time: formatHoursRange(day),
  }));
}

export function buildGroupedPublicHoursRows(weeklyHours = DEFAULT_WEEKLY_HOURS) {
  const rows = buildPublicHoursRows(weeklyHours);
  const grouped = [];

  for (const row of rows) {
    const previous = grouped[grouped.length - 1];
    if (previous && previous.time === row.time) {
      previous.days.push(row);
      continue;
    }
    grouped.push({ time: row.time, days: [row] });
  }

  return grouped.map((group) => {
    const first = group.days[0];
    const last = group.days[group.days.length - 1];
    const shortLabel = first.key === last.key ? first.shortDay : `${first.shortDay} - ${last.shortDay}`;
    const fullLabel = first.key === last.key ? first.day : `${first.day} - ${last.day}`;

    return {
      day: fullLabel,
      shortDay: shortLabel,
      time: group.time,
    };
  });
}

export async function fetchPublicAvailabilitySettings() {
  if (publicSettingsCache) {
    return publicSettingsCache;
  }

  if (!publicSettingsPromise) {
    publicSettingsPromise = fetchApi("/availability/settings/public")
      .then(async (response) => {
        if (!response.ok) {
          throw new Error("Failed to load public availability settings");
        }
        return response.json();
      })
      .then((settings) => {
        publicSettingsCache = settings;
        return settings;
      })
      .catch((error) => {
        console.error("Failed to load public availability settings:", error);
        publicSettingsCache = { weekly_hours: DEFAULT_WEEKLY_HOURS };
        return publicSettingsCache;
      })
      .finally(() => {
        publicSettingsPromise = null;
      });
  }

  return publicSettingsPromise;
}

export async function getPublicHoursRows() {
  const settings = await fetchPublicAvailabilitySettings();
  return buildPublicHoursRows(settings.weekly_hours);
}

export async function getGroupedPublicHoursRows() {
  const settings = await fetchPublicAvailabilitySettings();
  return buildGroupedPublicHoursRows(settings.weekly_hours);
}
